import { computed, ref, unref, watch } from 'vue';
import RealEstateBrokerCommissionsAPI from 'dashboard/api/realEstateBrokerCommissions';
import RealEstateBrokerCommissionRequestsAPI from 'dashboard/api/realEstateBrokerCommissionRequests';

export function useRealEstateBrokerCommissionRequest({
  installmentId,
  notify,
  t,
} = {}) {
  const detail = ref(null);
  const agreed = ref(false);
  const documentKind = ref('recibo');
  const file = ref(null);
  const loading = ref(false);
  const saving = ref(false);
  const uploadProgress = ref(0);
  const error = ref('');

  const request = computed(() => detail.value?.solicitacao || null);
  const canConfirm = computed(() =>
    Boolean(agreed.value && detail.value?.acoes?.pode_solicitar_transferencia)
  );
  const canSubmitDocument = computed(() =>
    Boolean(file.value && request.value?.acoes?.pode_enviar_nota)
  );

  const alert = (message, variant = 'error') => {
    error.value = variant === 'error' ? message : '';
    if (notify) notify(message, null, { variant });
  };

  const load = async () => {
    const id = unref(installmentId);
    if (!id) return;
    loading.value = true;
    error.value = '';
    try {
      const response = await RealEstateBrokerCommissionsAPI.detail(id);
      detail.value = response.data.data;
      documentKind.value = request.value?.nota?.tipo || 'recibo';
    } catch {
      alert(t('REAL_ESTATE_BROKER_COMMISSION.ERRORS.LOAD'));
    } finally {
      loading.value = false;
    }
  };

  const confirm = async () => {
    if (!canConfirm.value) return null;
    saving.value = true;
    try {
      const response = await RealEstateBrokerCommissionRequestsAPI.confirm(
        unref(installmentId),
        agreed.value
      );
      detail.value.solicitacao = response.data.data;
      alert(
        t('REAL_ESTATE_BROKER_COMMISSION.ALERTS.VALUE_CONFIRMED'),
        'success'
      );
      return response.data.data;
    } catch {
      alert(t('REAL_ESTATE_BROKER_COMMISSION.ERRORS.CONFIRM'));
      return null;
    } finally {
      saving.value = false;
    }
  };

  const submitDocument = async () => {
    if (!canSubmitDocument.value) return null;
    saving.value = true;
    uploadProgress.value = 0;
    try {
      const response =
        await RealEstateBrokerCommissionRequestsAPI.submitTaxDocument(
          request.value.id,
          {
            documentKind: documentKind.value,
            file: file.value,
            onUploadProgress: event => {
              uploadProgress.value = Math.round(
                (event.loaded / (event.total || event.loaded)) * 100
              );
            },
          }
        );
      detail.value.solicitacao = response.data.data;
      file.value = null;
      return response.data.data;
    } catch {
      alert(t('REAL_ESTATE_BROKER_COMMISSION.ERRORS.DOCUMENT'));
      return null;
    } finally {
      saving.value = false;
    }
  };

  const downloadDocument = async () => {
    const response =
      await RealEstateBrokerCommissionRequestsAPI.downloadTaxDocument(
        request.value.id
      );
    window.open(response.data.data.url, '_blank', 'noopener');
  };

  watch(() => unref(installmentId), load, { immediate: true });

  return {
    detail,
    request,
    agreed,
    documentKind,
    file,
    loading,
    saving,
    uploadProgress,
    error,
    canConfirm,
    canSubmitDocument,
    load,
    confirm,
    submitDocument,
    downloadDocument,
  };
}
